import { useEffect, useMemo, useRef } from 'react';
import {
  selectedAccountSelector,
  userMainAccStateSelector,
  selectedAccStateSelector,
} from '@core/store/library-stores/selectors';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { Button, ILayoutProps, Layout, LoadingData, useSetLayoutProps } from '@hwm/ui-lib';
import { makeStyles, Theme, Paper, Typography } from '@material-ui/core';
import { MenuOpen } from '@material-ui/icons';
import { SidebarKeys } from '../Sidebar/Sidebar.utils';
import { useSidebar } from '@shared/hooks';
import { Sidebar } from '../Sidebar';
import { AccountSitesIndicator } from '../AccountSitesIndicator';
import { Filters } from '@shared/components/Filters';
import {
  useSelectedSitesDataSource,
  useSitesDataSource,
  useSiteZonesDataSource,
} from '../Sidebar/SiteList/sitesDataSource';
import { ContextProviderComponent } from '@core/context';
import { Trans } from '@lingui/macro';
import { useHistory, useLocation } from 'react-router-dom';
import { isNil } from 'lodash';
import { useSiteListState } from '../Sidebar/SiteList/useSiteListFiltersState';
import { FilterType } from '@shared/models';
import { environment } from '@root/environment';
import { Helmet } from 'react-helmet';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';

interface SidebarModel {
  state: boolean;
  section: SidebarKeys;
  forceFavDisplay?: boolean;
}

export interface AppLayoutProps extends ILayoutProps {
  sidebar: SidebarModel;
}

export const TOGGLER_BTN_WIDTH = 48;
export const TOGGLER_BNT_HEIGHT = 44;

export const MENU_ICON = 36;

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    padding: 0,
    backgroundColor: 'white',
    height: TOGGLER_BNT_HEIGHT,
    marginLeft: theme.spacing(3),
    transition: 'width 0.3s',
    minWidth: 0,
    width: ({ show }: { show: boolean }) => (show ? 0 : TOGGLER_BTN_WIDTH),
    [theme.breakpoints.down('xs')]: {
      height: TOGGLER_BNT_HEIGHT - 4,
      width: ({ show }: { show: boolean }) => (show ? 0 : TOGGLER_BTN_WIDTH - 4),
    },
  },
  menuIcon: {
    transition: 'transform 0.3s',
    transform: ({ show }: { show: boolean }) => (show ? 'rotate(180deg)' : 'rotate(0)'),
    width: MENU_ICON,
    height: MENU_ICON,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      height: MENU_ICON - 4,
      width: MENU_ICON - 4,
    },
  },
  sidebarTransition: {
    transition: 'width 0.3s, opacity 0.4s',
  },
}));

const tailwindClasses = {
  sidebarHidden: 'w-0 p-0 m-0 opacity-40 ml-0',
  sidebarShown: 'w-full md:w-2/5 opacity-100 z-20 md:ml-2',
  sidebarShownDataSection: 'w-0 opacity-0 md:opacity-100 md:w-3/5 z-10',
};

export const AppLayout: React.FC<AppLayoutProps> = ({ children, sidebar: { state }, ...props }) => {
  const classes = useStyles({ show: state });

  const subaccountsFetchRef = useRef(null);

  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const siteIdsPermaNETRef = useRef(false);
  const { setSiteFilter } = useSiteListState();
  const [setLayoutProps] = useSetLayoutProps();

  const { id, name } = useAppSelector(selectedAccountSelector) ?? {};
  const { userRole } = useAppSelector(selectedAccStateSelector) ?? {};
  const { logoUrl, useZones } = useAppSelector(userMainAccStateSelector) ?? {};

  const { sidebar, handleSidebarState, handleForceFavDisplay } = useSidebar();

  const { handleGetUserSelectedSites, handleFavorites, totalAll, userSites, mainUserTotalSelected, loading } =
    useSitesDataSource();
  const { handleLoadSelectedList } = useSelectedSitesDataSource();

  const { getAccSiteZones } = useSiteZonesDataSource();

  const userRoleNoAccess = useMemo(() => userRole === null || userRole === 'No access', [userRole]);

  const userRoleAssigned = useMemo(() => !userRoleNoAccess && Boolean(userRole), [userRoleNoAccess, userRole]);

  useEffect(() => {
    if (id && mainUserTotalSelected === null && userRoleAssigned) {
      handleGetUserSelectedSites('', id);
    }
  }, [id, name, mainUserTotalSelected, userRoleAssigned, handleGetUserSelectedSites]);

  useEffect(() => {
    if (id && useZones && userRoleAssigned) {
      getAccSiteZones(id.toString());
    }
  }, [id, useZones, userRoleAssigned, getAccSiteZones]);

  useEffect(() => {
    setLayoutProps({
      urlLogo: logoUrl ?? `${process.env.PUBLIC_URL}/${environment.metaImg}.png`,
    });
  }, [logoUrl, setLayoutProps]);

  useEffect(() => {
    if (
      location?.search?.includes('siteId') &&
      !loading &&
      !isNil(totalAll) &&
      !isNil(mainUserTotalSelected) &&
      !siteIdsPermaNETRef.current
    ) {
      siteIdsPermaNETRef.current = true;

      const siteIds =
        location?.search
          .split('&')
          .filter((el) => el.includes('siteId'))
          .map((el) => el.split('=')?.[1]) ?? [];

      handleLoadSelectedList(
        null,
        () => {
          siteIds?.forEach((siteId) => {
            handleFavorites(true, Number(siteId), true);
          });

          setTimeout(() => {
            history.replace('');
            handleForceFavDisplay(true);
          }, 0);
        },
        true
      );
    }
  }, [
    loading,
    history,
    totalAll,
    location?.search,
    mainUserTotalSelected,
    dispatch,
    setSiteFilter,
    handleFavorites,
    handleForceFavDisplay,
    handleLoadSelectedList,
  ]);

  useEffect(() => {
    if (sidebar?.forceFavDisplay) {
      setSiteFilter(FilterType.Selected);
      handleForceFavDisplay(false);
    }
  }, [sidebar?.forceFavDisplay, setSiteFilter, handleForceFavDisplay]);

  useEffect(() => {
    if (!id || subaccountsFetchRef.current) return;

    subaccountsFetchRef.current = true;
    dispatch(
      accountsThunks.fetchSubAccounts({
        accountId: id.toString(),
      })
    );
  }, [dispatch, id, setLayoutProps]);

  return (
    <Layout {...props}>
      <Helmet>
        <title>{environment.metaTitle}</title>

        <meta name="title" content={environment.metaTitle} />
        <meta name="description" content={environment.metaDescription} />

        <meta property="og:title" content={environment.metaTitle} />
        <meta property="og:description" content={environment.metaDescription} />
        <meta property="og:url" content={environment.metaUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={process.env.PUBLIC_URL + `/${environment.metaImg}.png`} />
        <meta property="og:image:alt" content={environment.metaImgAlt} />
        <meta property="og:image:secure_url" content={process.env.PUBLIC_URL + `/${environment.metaImg}.png`} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="135" />
        <meta property="og:image:height" content="51" />

        <meta property="twitter:title" content={environment.metaTitle} />
        <meta property="twitter:description" content={environment.metaDescription} />
        <meta property="twitter:url" content={environment.metaUrl} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:image" content={process.env.PUBLIC_URL + `/${environment.metaImg}.png`} />
      </Helmet>

      {userRoleNoAccess ? (
        <div className="flex items-center justify-center w-full h-full text-center">
          <Typography variant="h5">
            <Trans>You do not have permission to view content. Please contact your administrator.</Trans>
          </Typography>
        </div>
      ) : id && name && userRoleAssigned ? (
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between relative -top-1.5 h-10 items-end mr-0.5">
            <Filters />
            <div className="flex flex-row justify-center items-center relative">
              <AccountSitesIndicator accName={name} selectedSites={userSites} />
              <Button
                color="inherit"
                variant="contained"
                className={`${classes.button}`}
                style={{}}
                onClick={() => handleSidebarState(true)}
              >
                <MenuOpen classes={{ root: classes.menuIcon }} />
              </Button>
            </div>
          </div>
          <div className="flex flex-row w-full h-full overflow-x-hidden overflow-y-hidden pb-0.5">
            <div
              className={`h-full flex flex-col ${
                state ? tailwindClasses.sidebarShownDataSection : 'w-full'
              } ${classes.sidebarTransition}`}
            >
              {children}
            </div>
            <div
              className={`h-full flex ${
                state ? tailwindClasses.sidebarShown : tailwindClasses.sidebarHidden
              } ${classes.sidebarTransition}`}
            >
              <Paper className="w-full p-2">
                <Sidebar />
              </Paper>
            </div>
          </div>
        </div>
      ) : (
        <LoadingData />
      )}
    </Layout>
  );
};

export const AppLayoutWrapper: React.FC<AppLayoutProps> = (props) => {
  return (
    <ContextProviderComponent>
      <AppLayout {...props} />
    </ContextProviderComponent>
  );
};
